import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Building, MapPin, User, Users, X, ChevronRight, ChevronDown, Plus } from 'lucide-react';
import '../styles/general/SearchModal.css';

const TYPE_META = {
  unit:     { label: 'Unit',      icon: Building, color: '#6366f1' },
  town:     { label: 'Town',      icon: MapPin,   color: '#10b981' },
  officer:  { label: 'Officer',   icon: Users,    color: 'var(--accent)' },
  employee: { label: 'Employee',  icon: User,     color: 'var(--accent-cyan)' },
};

// People ride at the bottom, collapsed — units are the primary surface.
const COLLAPSED_TYPES = ['officer', 'employee'];

const pad4 = (v) => (v === null || v === undefined || v === '' ? '' : String(v).padStart(4, '0'));
const unitCode = (local, unit) => {
  const l = pad4(local);
  const u = pad4(unit);
  if (l && u) return `${l}-${u}`;
  return l || u || '';
};

function resultPath(r) {
  if (r.type === 'employee') return `/employee/${r.id}`;
  if (r.type === 'officer')  return r.unitId ? `/units/${r.unitId}` : '/staff';
  if (r.type === 'unit')     return `/units/${r.id}`;
  if (r.type === 'town')     return `/town/${encodeURIComponent(r.name)}`;
  return '/';
}

function resultSubtitle(r) {
  if (r.type === 'officer') {
    const code = unitCode(r.unitLocal, r.unitUnit);
    return [r.role, code ? `${code} · ${r.unitName || ''}`.trim() : r.unitName].filter(Boolean).join(' · ');
  }
  if (r.type === 'unit') return [unitCode(r.local, r.unit), r.town].filter(Boolean).join(' · ');
  if (r.type === 'town') return 'Town';
  return null;
}

export default function SearchModal({ open, onClose }) {
  const [query, setQuery]     = useState('');
  const [results, setResults] = useState([]);
  const [counts, setCounts]   = useState({});
  const [unitsLimit, setUnitsLimit] = useState(10);
  const [peopleLimit, setPeopleLimit] = useState(5);
  const [expanded, setExpanded] = useState({ officer: false, employee: false });
  const [loading, setLoading] = useState(false);
  const [cursor, setCursor]   = useState(-1);
  const inputRef   = useRef(null);
  const listRef    = useRef(null);
  const debounceRef = useRef(null);
  const navigate   = useNavigate();

  // Focus input when modal opens
  useEffect(() => {
    if (open) {
      setQuery('');
      setResults([]);
      setCounts({});
      setUnitsLimit(10);
      setPeopleLimit(5);
      setExpanded({ officer: false, employee: false });
      setCursor(-1);
      setTimeout(() => inputRef.current?.focus(), 30);
    }
  }, [open]);

  // Reset limits/expansion when the query itself changes
  useEffect(() => {
    setUnitsLimit(10);
    setPeopleLimit(5);
    setExpanded({ officer: false, employee: false });
  }, [query]);

  // Debounced search — refires when limits grow ("See more")
  useEffect(() => {
    if (!open) return;
    clearTimeout(debounceRef.current);
    if (query.length < 2) { setResults([]); setCounts({}); return; }

    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const token = getToken();
        const res = await fetch(
          `/api/search?q=${encodeURIComponent(query)}&units_limit=${unitsLimit}&people_limit=${peopleLimit}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const data = await res.json();
        setResults(data.results || []);
        setCounts(data.counts || {});
        setCursor(-1);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 220);

    return () => clearTimeout(debounceRef.current);
  }, [query, open, unitsLimit, peopleLimit]);

  const go = useCallback((r) => {
    navigate(resultPath(r));
    onClose();
  }, [navigate, onClose]);

  const getToken = () =>
    localStorage.getItem('token') || sessionStorage.getItem('token');

  // Visible (navigable) results: everything except collapsed people groups
  const visibleResults = results.filter(
    (r) => !COLLAPSED_TYPES.includes(r.type) || expanded[r.type]
  );

  // Keyboard navigation over visible results
  const handleKey = (e) => {
    if (e.key === 'Escape') { onClose(); return; }
    if (visibleResults.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setCursor(c => Math.min(c + 1, visibleResults.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setCursor(c => Math.max(c - 1, 0));
    } else if (e.key === 'Enter' && cursor >= 0) {
      e.preventDefault();
      go(visibleResults[cursor]);
    }
  };

  useEffect(() => {
    if (cursor >= 0 && listRef.current) {
      const item = listRef.current.querySelector(`[data-idx="${cursor}"]`);
      item?.scrollIntoView({ block: 'nearest' });
    }
  }, [cursor]);

  if (!open) return null;

  const groups = Object.keys(TYPE_META).reduce((acc, type) => {
    const items = results.filter(r => r.type === type);
    if (items.length) acc.push({ type, items });
    return acc;
  }, []);

  const peopleShown = results.filter(r => COLLAPSED_TYPES.includes(r.type)).length;
  const countFor = (type) =>
    type === 'unit' ? counts.units : type === 'officer' ? counts.officers : type === 'employee' ? counts.employees : undefined;

  return (
    <div className="search-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="search-dialog" onKeyDown={handleKey}>

        {/* Input row */}
        <div className="search-input-row">
          <Search size={16} className="search-icon" />
          <input
            ref={inputRef}
            className="search-input"
            type="text"
            placeholder="Search units, chapters (1303-0064), towns, people…"
            value={query}
            onChange={e => setQuery(e.target.value)}
            autoComplete="off"
            spellCheck={false}
          />
          {loading && <span className="search-spinner" />}
          {!loading && query && (
            <button className="search-clear" onClick={() => { setQuery(''); setResults([]); inputRef.current?.focus(); }}>
              <X size={14} />
            </button>
          )}
          <kbd className="search-esc">esc</kbd>
        </div>

        {/* Results */}
        {query.length >= 2 && (
          <div className="search-body" ref={listRef}>
            {groups.length === 0 && !loading && (
              <div className="search-empty">No results for <strong>"{query}"</strong></div>
            )}

            {groups.map(({ type, items }) => {
              const meta = TYPE_META[type];
              const Icon = meta.icon;
              const collapsed = COLLAPSED_TYPES.includes(type) && !expanded[type];
              const total = countFor(type);

              if (collapsed) {
                return (
                  <div key={type} className="search-group">
                    <button
                      className="search-group-label search-group-toggle"
                      onClick={() => setExpanded((p) => ({ ...p, [type]: true }))}
                      style={{ width: '100%', textAlign: 'left', cursor: 'pointer', background: 'none', border: 'none', display: 'flex', alignItems: 'center', gap: 6 }}
                    >
                      <ChevronRight size={12} />
                      <Icon size={11} />
                      {meta.label}s {typeof total === 'number' ? `(${total})` : `(${items.length})`}
                      <span style={{ marginLeft: 'auto', fontSize: 11, opacity: 0.7 }}>show</span>
                    </button>
                  </div>
                );
              }

              return (
                <div key={type} className="search-group">
                  {COLLAPSED_TYPES.includes(type) ? (
                    <button
                      className="search-group-label search-group-toggle"
                      onClick={() => setExpanded((p) => ({ ...p, [type]: false }))}
                      style={{ width: '100%', textAlign: 'left', cursor: 'pointer', background: 'none', border: 'none', display: 'flex', alignItems: 'center', gap: 6 }}
                    >
                      <ChevronDown size={12} />
                      <Icon size={11} />
                      {meta.label}s {typeof total === 'number' ? `(${total})` : ''}
                      <span style={{ marginLeft: 'auto', fontSize: 11, opacity: 0.7 }}>hide</span>
                    </button>
                  ) : (
                    <div className="search-group-label">
                      <Icon size={11} />
                      {meta.label}s{typeof total === 'number' && total > items.length ? ` — showing ${items.length} of ${total}` : ''}
                    </div>
                  )}
                  {items.map((r) => {
                    const globalIdx = visibleResults.indexOf(r);
                    const subtitle = resultSubtitle(r);
                    return (
                      <button
                        key={`${r.type}-${r.id ?? r.name}`}
                        className={`search-result${cursor === globalIdx && globalIdx >= 0 ? ' active' : ''}`}
                        data-idx={globalIdx}
                        onMouseEnter={() => globalIdx >= 0 && setCursor(globalIdx)}
                        onClick={() => go(r)}
                      >
                        <span className="search-result-icon" style={{ color: meta.color }}>
                          <Icon size={14} />
                        </span>
                        <span className="search-result-body">
                          <span className="search-result-name">
                            {r.type === 'unit' && unitCode(r.local, r.unit) ? (
                              <span style={{ fontFamily: 'var(--font-mono, monospace)', fontSize: 12, marginRight: 8, opacity: 0.85 }}>
                                {unitCode(r.local, r.unit)}
                              </span>
                            ) : null}
                            {r.name}
                          </span>
                          {subtitle && <span className="search-result-sub">{subtitle}</span>}
                        </span>
                        <span className="search-result-type">{meta.label}</span>
                      </button>
                    );
                  })}
                  {/* See more — units expand in place */}
                  {type === 'unit' && typeof total === 'number' && total > items.length && (
                    <button
                      className="search-result"
                      onClick={() => setUnitsLimit((l) => l + 15)}
                      style={{ justifyContent: 'center', fontWeight: 600 }}
                    >
                      <Plus size={13} style={{ marginRight: 6 }} />
                      See more units ({total - items.length} remaining)
                    </button>
                  )}
                  {COLLAPSED_TYPES.includes(type) && expanded[type] && typeof total === 'number' && total > items.length && (
                    <button
                      className="search-result"
                      onClick={() => setPeopleLimit((l) => l + 10)}
                      style={{ justifyContent: 'center', fontWeight: 600 }}
                    >
                      <Plus size={13} style={{ marginRight: 6 }} />
                      See more ({total - items.length} remaining)
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Footer hint */}
        <div className="search-footer">
          <span><kbd>↑↓</kbd> navigate</span>
          <span><kbd>↵</kbd> open</span>
          <span><kbd>esc</kbd> close</span>
          {peopleShown > 0 && <span style={{ marginLeft: 'auto' }}>People results are grouped below units</span>}
        </div>
      </div>
    </div>
  );
}
